import scrollIntoView from '../../Vendor/ScrollIntoViewIfNeeded'
import { EventNames } from '../constants/EventNames'
import { IsVarEmpty } from '../utils/GlobalFunctions'

export enum DOMElements {
    TABLE = 'table',
    TABLE_ROW = 'tr',
    TABLE_DATA = 'td',
    DIVISION = 'div',
    SELECT = 'select',
    OPTION = 'option',
    UNORDERED_LIST = 'ul',
    LIST_ITEM = 'li',
    ANCHOR = 'a',
    HEADER_TWO = 'h2',
    INPUT = 'input',
    SCRIPT = 'script',
    BUTTON = 'button'
}

export const createElement = <K extends keyof HTMLElementTagNameMap>(
    element: K,
    ...classNames: string[]
): HTMLElementTagNameMap[K] => {
    const elem: HTMLElementTagNameMap[K] = document.createElement(element)

    if (!IsVarEmpty(classNames)) {
        classNames.forEach((className: string): void => elem.classList.add(className))
    }

    return elem
}

export type StandardCreateElementProps = {
    content?: string
    classes?: string[]
}

export const createTable = (...classNames: string[]): HTMLTableElement =>
    createElement(DOMElements.TABLE, ...classNames)
export const createTableRow = (...classNames: string[]): HTMLTableRowElement =>
    createElement(DOMElements.TABLE_ROW, ...classNames)
export const createTableData = (...classNames: string[]): HTMLTableDataCellElement =>
    createElement(DOMElements.TABLE_DATA, ...classNames)
export const createDiv = (...props: [StandardCreateElementProps] | string[]): HTMLDivElement => {
    if (Array.isArray(props) && typeof props[0] === 'string') {
        return createElement(DOMElements.DIVISION, ...(props as string[]))
    }

    const objProps: StandardCreateElementProps = props[0] as StandardCreateElementProps

    const div: HTMLDivElement = createElement(DOMElements.DIVISION, ...(objProps?.classes ?? []))

    div.innerHTML = objProps?.content ?? ''

    return div
}
export const createSelect = (...classNames: string[]): HTMLSelectElement =>
    createElement(DOMElements.SELECT, ...classNames)
export const createOption = (...classNames: string[]): HTMLOptionElement =>
    createElement(DOMElements.OPTION, ...classNames)
export const createUnorderedList = (...classNames: string[]): HTMLUListElement =>
    createElement(DOMElements.UNORDERED_LIST, ...classNames)
export const createListItem = (...classNames: string[]): HTMLLIElement =>
    createElement(DOMElements.LIST_ITEM, ...classNames)
export const createAnchor = (...classNames: string[]): HTMLAnchorElement =>
    createElement(DOMElements.ANCHOR, ...classNames)
export const createHeaderTwo = (...classNames: string[]): HTMLHeadingElement =>
    createElement(DOMElements.HEADER_TWO, ...classNames)
export const createInput = (...classNames: string[]): HTMLInputElement =>
    createElement(DOMElements.INPUT, ...classNames)

export type ButtonProps = StandardCreateElementProps & { onClick?: (event: MouseEvent) => void }

export const createButton = (props: ButtonProps): HTMLButtonElement => {
    const button: HTMLButtonElement = createElement(DOMElements.BUTTON, ...(props?.classes ?? []))

    button.innerText = props?.content ?? ''

    if (!IsVarEmpty(props.onClick)) {
        button.addEventListener(EventNames.CLICK, props.onClick)
    }

    return button
}

export type ScriptProps = {
    src?: string
    async?: boolean
    defer?: boolean
    content?: string
}

export const createScript = ({ src, async, defer, content }: ScriptProps): HTMLScriptElement => {
    const script: HTMLScriptElement = createElement(DOMElements.SCRIPT)

    if (!IsVarEmpty(src)) {
        script.setAttribute('src', src)
    }

    if (!IsVarEmpty(async)) {
        script.setAttribute('async', `${async}`)
    }

    if (!IsVarEmpty(defer)) {
        script.setAttribute('defer', `${defer}`)
    }

    if (!IsVarEmpty(content)) {
        script.innerHTML = content
    }

    return script
}

// Create Div before a given element selector
export const createDivBefore = (selector: string, ...classNames: string[]): HTMLDivElement => {
    const div: HTMLDivElement = createDiv(...classNames)

    const elem: OnlyNullable<Element> = document.querySelector(selector)

    if (IsVarEmpty(elem)) {
        throw new Error(`Could not find element with selector ${selector}`)
    }

    elem.before(div)

    return div
}

export const getMaxMinRectFromElements = (elements: Nullable<HTMLElement[]>): DOMRect => {
    if (elements) {
        const rects: DOMRect[] = elements.map((element: HTMLElement): DOMRect => element.getBoundingClientRect())
        const maxRect: DOMRect = rects[0]

        rects.forEach((rect: DOMRect): void => {
            const maxRight: number = Math.max(maxRect.right, rect.right)
            const maxBottom: number = Math.max(maxRect.bottom, rect.bottom)

            maxRect.x = Math.min(maxRect.x, rect.x)
            maxRect.y = Math.min(maxRect.y, rect.y)

            maxRect.width = maxRight - maxRect.x
            maxRect.height = maxBottom - maxRect.y
        })

        return maxRect
    }

    return new DOMRect()
}

export const isDescendant = (parent: Element, child: Element): boolean => {
    let node: OnlyNullable<Node> = child

    while (node) {
        if (node === parent) {
            return true
        }

        // Traverse up to the parent
        node = node.parentNode
    }

    // Go up until the root but couldn't find the `parent`
    return false
}

export const isElementVisible = (elem: HTMLElement, ...allowedTopLevelElementSelectors: string[]): boolean => {
    const style: CSSStyleDeclaration = getComputedStyle(elem)

    // If the element is off the screen then scroll it into view.
    scrollIntoView(elem, {
        scrollMode: 'if-needed'
    })

    if (
        style.display === 'none' ||
        style.visibility !== 'visible' ||
        elem.offsetWidth +
            elem.offsetHeight +
            elem.getBoundingClientRect().height +
            elem.getBoundingClientRect().width ===
            0
    ) {
        return false
    }

    const elemCenter: {
        x: number
        y: number
    } = {
        x: elem.getBoundingClientRect().left + elem.offsetWidth / 2,
        y: elem.getBoundingClientRect().top + elem.offsetHeight / 2
    }

    if (
        elemCenter.x < 0 ||
        elemCenter.x > (document.documentElement.clientWidth || window.innerWidth) ||
        elemCenter.y < 0 ||
        elemCenter.y > (document.documentElement.clientHeight || window.innerHeight)
    ) {
        return false
    }

    const pointContainer: Nullable<Element> = document.elementFromPoint(elemCenter.x, elemCenter.y)

    if (pointContainer) {
        if (
            isDescendant(elem, pointContainer) ||
            allowedTopLevelElementSelectors.some(
                (allowedTopLevelElementSelector: string): boolean =>
                    !!pointContainer?.closest(allowedTopLevelElementSelector)
            )
        ) {
            return true
        }
    }

    return false
}

export const areElementsVisible = (elements: HTMLElement[], ...allowedTopLevelElementSelector: string[]): boolean => {
    if (elements.length === 0) {
        return false
    }

    return elements.every((element: HTMLElement): boolean =>
        isElementVisible(element, ...allowedTopLevelElementSelector)
    )
}

export const bodyFinder = (): Promise<Nullable<HTMLElement>> => {
    const body: HTMLElement = document.body

    if (body) {
        return Promise.resolve(body)
    }

    return new Promise<Nullable<HTMLElement>>((resolve) => {
        window.addEventListener('DOMContentLoaded', (): void => resolve(bodyFinder()))
    })
}
